import { useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent } from '@mui/material';
import { TextBlock } from '../../../Atoms/Text';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { fetchAdviseRequirementSheet } from '../../../../Api/Advise/FeatureInput';
import { downloadBlob } from '../../../../utils/fileDownload';
import { isNotFoundRefusal } from '../../../../utils/refusalMessage';
import { FeatureInputSupplier, RequiredFeatureCategory } from '../../../../types/advise';
import { requiredFeaturesSummary, supplierRefusal } from './SupplierFeatureInput.calc';
import { SubtitledDialogTitle } from './SubtitledDialogTitle';
import { ShortlistSupplierPicker } from './ShortlistSupplierPicker';

const EXPORT_FAILED = "Couldn't export the requirement sheet. Please try again.";

interface ExportRequirementSheetDialogProps {
  engagementId: string;
  engagementName: string;
  suppliers: FeatureInputSupplier[];
  supplierId: string;
  requiredCategories: RequiredFeatureCategory[];
  onClose: () => void;
  // Called once the file has downloaded, so the supplier's card can show the export.
  onExported: () => void;
  // Called in place of a refusal when the API says the supplier has left the shortlist.
  onShortlistChanged: () => void;
}

/** Exports one supplier's requirement sheet; every supplier gets a file of their own. */
export const ExportRequirementSheetDialog = ({
  engagementId,
  engagementName,
  suppliers,
  supplierId: initialSupplierId,
  requiredCategories,
  onClose,
  onExported,
  onShortlistChanged
}: ExportRequirementSheetDialogProps) => {
  const [supplierId, setSupplierId] = useState(initialSupplierId);
  const [exporting, setExporting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const supplier = suppliers.find((row) => row.id === supplierId);

  const handleExport = async () => {
    if (!supplier) return;
    setExporting(true);
    setRefusal(null);
    const { data, error } = await fetchAdviseRequirementSheet(engagementId, supplier.id);
    setExporting(false);
    if (isNotFoundRefusal(error)) return onShortlistChanged();
    if (error || !data) return setRefusal(supplierRefusal(error, EXPORT_FAILED));
    downloadBlob(data.blob, data.filename ?? 'requirement-sheet.xlsx');
    onExported();
    onClose();
  };

  return (
    <Dialog
      open
      onClose={exporting ? undefined : onClose}
      maxWidth="sm"
      fullWidth>
      <SubtitledDialogTitle
        title="Export Requirement Sheet"
        subtitle={`${engagementName} · Feature Responses`}
        busy={exporting}
        onClose={onClose}
      />
      <DialogContent className="flex flex-col gap-4">
        <Alert
          severity="info"
          icon={false}>
          Generates a single .xlsx sheet for the selected supplier only. Each supplier gets their own file — send it
          directly to them, never a shared file.
        </Alert>
        {refusal && <Alert severity="error">{refusal}</Alert>}
        <ShortlistSupplierPicker
          suppliers={suppliers}
          value={supplierId}
          onChange={setSupplierId}
        />
        <div>
          <TextBlock
            size="sm"
            weight="semibold">
            {requiredFeaturesSummary(requiredCategories)}
          </TextBlock>
          <TextBlock
            size="sm"
            color="grey6">
            Pulled from the features ticked on the Functionality Matrix.
          </TextBlock>
        </div>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={onClose}
          disabled={exporting}>
          Cancel
        </button>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={handleExport}
          disabled={!supplier || exporting}>
          {exporting ? <ComponentSpinner sx={{ height: '19px' }} /> : 'Export Sheet (.xlsx)'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
